package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.CreateIrrigationRequest
import or.garden.manager.domain.model.IrrigationResponse

internal class IrrigationApi {
    suspend fun getIrrigation(): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get(
            "${GardenHttpClient.baseUrl}/irrigation"
        )
    }

    suspend fun createIrrigation(irrigation: CreateIrrigationRequest): Result<HttpResponse> =
        runCatching {
            return@runCatching GARDEN_CLIENT.post(
                "${GardenHttpClient.baseUrl}/irrigation"
            ) {
                setBody(irrigation)
            }
        }

    suspend fun deleteIrrigation(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.delete(
            "${GardenHttpClient.baseUrl}/irrigation/$id"
        )
    }

    suspend fun markAsWatered(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.put(
            "${GardenHttpClient.baseUrl}/irrigation/$id/watered"
        )
    }
}

private data object IrrigationRepository {
    fun getIrrigation(): Flow<ResultState<ApiResponse<List<IrrigationResponse>>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = IrrigationApi()
                .getIrrigation()
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to load irrigation data. Please try again!"))
        }
    }

    fun createIrrigation(irrigation: CreateIrrigationRequest): Flow<ResultState<ApiResponse<CreateIrrigationRequest>>> =
        flow {
            emit(ResultState.Loading)
            delay(GardenHttpClient.apiDelayMillis)
            try {
                val response = IrrigationApi()
                    .createIrrigation(irrigation)
                    .getOrNull()
                if (response != null && response.status.isSuccess()) {
                    emit(ResultState.Success(response.body()))
                } else {
                    emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
                }
            } catch (e: Exception) {
                emit(ResultState.Error("Failed to create irrigation schedule. Please try again!"))
            }
        }

    fun deleteIrrigation(id: Int): Flow<ResultState<ApiResponse<String>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = IrrigationApi()
                .deleteIrrigation(id)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to delete irrigation schedule. Please try again!"))
        }
    }

    fun markAsWatered(id: Int): Flow<ResultState<ApiResponse<IrrigationResponse>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = IrrigationApi()
                .markAsWatered(id)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error("Failed to mark irrigation as watered. Please try again!"))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("Failed to mark irrigation as watered. Please try again!"))
        }
    }
}

data object IrrigationService {
    fun getIrrigation(result: (ResultState<ApiResponse<List<IrrigationResponse>>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            IrrigationRepository
                .getIrrigation()
                .collect {
                    result(it)
                }
        }
    }

    fun createIrrigation(
        irrigation: CreateIrrigationRequest,
        result: (ResultState<ApiResponse<CreateIrrigationRequest>>) -> Unit
    ) {
        CoroutineScope(Dispatchers.Default).launch {
            IrrigationRepository
                .createIrrigation(irrigation)
                .collect {
                    result(it)
                }
        }
    }

    fun deleteIrrigation(id: Int, result: (ResultState<ApiResponse<String>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            IrrigationRepository
                .deleteIrrigation(id)
                .collect {
                    result(it)
                }
        }
    }

    fun markAsWatered(id: Int, result: (ResultState<ApiResponse<IrrigationResponse>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            IrrigationRepository
                .markAsWatered(id)
                .collect {
                    result(it)
                }
        }
    }
}
